import EmbedBase from "@editorjs/embed"

/**
 * `@editorjs/embed` is built for pasted URLs — with no `service` it renders an empty node
 * (toolbox inserts look broken). Adding a URL field matches how authors expect the + menu
 * to behave. Paste still works via `onPaste` on https://github.com/editor-js/embed
 */
export default class EmbedTool extends EmbedBase {
  static get toolbox() {
    return {
      title: "Embed",
      icon: '<span class="icon-[lucide--youtube] size-5" aria-hidden="true"></span>'
    }
  }

  /**
   * @param {Record<string, unknown>} savedData
   * @returns {boolean}
   */
  validate(savedData) {
    const d = savedData || {}
    return Boolean(d.service && d.embed && String(d.embed).trim().length > 0)
  }

  render() {
    if (this.data?.service) {
      return super.render()
    }

    if (this.readOnly) {
      const el = document.createElement("div")
      el.classList.add(this.api.styles.block, "text-sm", "text-base-content/60", "py-2")
      el.textContent = "Embed"
      this.element = el
      return el
    }

    const wrap = document.createElement("div")
    wrap.classList.add(
      this.api.styles.block,
      "embed-tool-stub",
      "rounded-box",
      "border",
      "border-base-content/20",
      "bg-base-200/40",
      "p-4"
    )

    const hint = document.createElement("p")
    hint.className = "mb-2 text-sm text-base-content/70"
    hint.textContent = "Paste a supported URL or type it, then press Enter or click Embed."

    const input = document.createElement("input")
    input.type = "url"
    input.className = "input input-bordered input-sm w-full"
    input.placeholder = "https://www.youtube.com/watch?v=…"
    input.autocomplete = "off"

    const err = document.createElement("p")
    err.className = "mt-2 hidden text-sm text-error"
    err.role = "alert"

    const apply = () => {
      err.classList.add("hidden")
      const url = input.value.trim()
      if (!url) return
      if (this.tryApplyEmbedUrl(url)) return
      err.textContent =
        "That URL did not match a supported provider (YouTube, Vimeo, etc.). Paste a standard share link."
      err.classList.remove("hidden")
    }

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault()
        event.stopPropagation()
        apply()
      }
    })

    const btnRow = document.createElement("div")
    btnRow.className = "mt-3 flex flex-wrap items-center gap-2"

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "btn btn-primary btn-sm"
    btn.textContent = "Embed"
    btn.addEventListener("click", apply)

    btnRow.appendChild(btn)
    wrap.appendChild(hint)
    wrap.appendChild(input)
    wrap.appendChild(err)
    wrap.appendChild(btnRow)

    this.element = wrap
    requestAnimationFrame(() => input.focus())

    return wrap
  }

  tryApplyEmbedUrl(url) {
    const services = EmbedBase.services
    if (!services || !url) return false

    for (const [serviceKey, definition] of Object.entries(services)) {
      const def = definition
      const regex = def?.regex
      if (!regex || typeof regex.exec !== "function") continue

      const match = regex.exec(url)
      if (!match) continue

      const groups = match.slice(1)
      const {
        embedUrl,
        width,
        height,
        id = (g) => g.shift() || ""
      } = def

      const embed =
        groups.length > 0 ? embedUrl.replace(/<%= remote_id %>/g, id(groups)) : ""
      if (!embed) continue

      this.data = {
        service: serviceKey,
        source: url,
        embed,
        width,
        height
      }
      return true
    }

    return false
  }
}
